import { type Call, useCalls } from '@hostesa/api-client';
import { useMemo } from 'react';
import { bucketCallsByDay, weekOverWeekDelta } from './call-buckets.ts';
import type { KpiMetric, PresentationResult, ReceptionDeskVM } from './types.ts';

const DESK_WINDOW_DAYS = 14;
/** Em dash for a KPI with no real value yet (never a fabricated number). */
const NO_VALUE = '—';

function formatMmSs(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.round(totalSeconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function avgDuration(calls: readonly Call[]): number | null {
  let total = 0;
  let count = 0;
  for (const c of calls) {
    if (typeof c.durationSeconds === 'number' && Number.isFinite(c.durationSeconds)) {
      total += c.durationSeconds;
      count += 1;
    }
  }
  return count > 0 ? total / count : null;
}

function languageCodes(calls: readonly Call[]): string[] {
  const set = new Set<string>();
  for (const c of calls) {
    if (typeof c.language === 'string' && c.language.trim()) {
      set.add(c.language.trim().toUpperCase());
    }
  }
  return [...set];
}

/** A sparkline is only worth showing once there is real daily data. */
function trendOrNone(series: number[], hasData: boolean): number[] | undefined {
  return hasData ? series : undefined;
}

/**
 * Reception KPI view-model — every figure is real or absent. Counts (calls
 * handled, bookings captured, languages, average handling time) come from the
 * canonical `useCalls` query. Deltas are week-over-week from the same fetched
 * window, shown only when both weeks have data; sparklines are the real daily
 * buckets. Nothing is fabricated — when the desk is quiet the numbers read zero
 * and the handling time is an em dash.
 */
export function useReceptionDesk(
  tenantId: string,
  hotelId: string,
  timeZone: string,
): PresentationResult<ReceptionDeskVM> {
  const calls = useCalls(tenantId, hotelId, { limit: 50 });
  const data = useMemo<ReceptionDeskVM | undefined>(() => {
    if (!calls.data) return undefined;
    const list = calls.data.items;
    const hasData = list.length > 0;
    const completed = list.filter((c) => c.status === 'completed').length;
    const codes = languageCodes(list);
    const avg = avgDuration(list);
    const buckets = bucketCallsByDay(list, timeZone, DESK_WINDOW_DAYS);

    const callsHandled: KpiMetric = {
      value: String(list.length),
      delta: weekOverWeekDelta(buckets.all),
      trend: trendOrNone(buckets.all, hasData),
    };
    const bookingsCaptured: KpiMetric = {
      value: String(completed),
      delta: weekOverWeekDelta(buckets.completed),
      trend: trendOrNone(buckets.completed, hasData),
    };
    return {
      callsHandled,
      bookingsCaptured,
      avgHandlingTime: { value: avg !== null ? formatMmSs(avg) : NO_VALUE },
      languagesServed: { value: String(codes.length), codes },
    };
  }, [calls.data, timeZone]);
  return { data, isLoading: calls.isLoading, isError: calls.isError };
}
